import TransportWebUSB from '@ledgerhq/hw-transport-webusb'
import Eth from '@ledgerhq/hw-app-eth'
import { ethers } from 'ethers'
import localeAbi from '@/connect/abi/index.js'
import { ErrorInfo, errCodeHandle } from '@/tools/error'

const abi = localeAbi[process.env.VUE_APP_CHAIN]

import { Wallet } from '@/connect/wallet/wallet'

import UAParser from 'ua-parser-js'

// 路径类型
const pathTypeArray = [
  {
    type: `m/44'/60'/0'`,
    placeholder: `m/44'/60'/0'/i`
  },
  {
    type: `m/44'/60'`,
    placeholder: `m/44'/60'/i'/0/0`
  }
]

const pathTypeObj = {}

pathTypeArray.forEach(item => {
  pathTypeObj[item.type] = item.placeholder
})

function throwError(code, error) {
  switch (code) {
    case 'browser_not_support':
      errCodeHandle.go(240005)
      return ErrorInfo(240005)
    case 26368:
      errCodeHandle.go(240002)
      return ErrorInfo(240002)
    case 27904:
      errCodeHandle.go(240003)
      return ErrorInfo(240003)
    case 26628:
      errCodeHandle.go(240004)
      return ErrorInfo(240004)
    case 'No device selected.':
      errCodeHandle.go(240001)
      return ErrorInfo(240001)
    case 'Failure_ActionCancelled':
      errCodeHandle.go(250009)
      return ErrorInfo(250009)
    case 27013:
      errCodeHandle.go(230001)
      return ErrorInfo(230001)
    case 'EthAppPleaseEnableContractData':
      errCodeHandle.go(240007)
      return ErrorInfo(240007)
    case 'TransportError':
      errCodeHandle.go(240006)
      return ErrorInfo(240006)
    case 'DisconnectedDeviceDuringOperation':
      errCodeHandle.go(240008)
      return ErrorInfo(240008)
    case 'TransportInterfaceNotAvailable':
      errCodeHandle.go(240009)
      return ErrorInfo(240009)
    default:
      console.log(error)
      errCodeHandle.go(400001)
      return ErrorInfo(400001)
  }
}

export class Ledger extends Wallet {
  constructor() {
    super()
    this._paths = {}
    this.PATHTYPEARRAY = pathTypeArray
    this.PATHTYPEOBJ = pathTypeObj
  }

  async connect() {
    const parser = new UAParser()
    const browser = parser.getBrowser()
    if (browser.name !== 'Chrome') {
      throw throwError('browser_not_support')
    }
    let transport
    try {
      transport = await TransportWebUSB.create()
    } catch (err) {
      if (err.statusCode !== undefined) {
        throw throwError(err.statusCode, err)
      } else if (err.message === 'No device selected.') {
        throw throwError(err.message, err)
      } else {
        throw throwError(err.name, err)
      }
    }
    this._connector = new Eth(transport)
    const account = await this.getAccount(this.PATHTYPEARRAY[0].type, 0)
    this.account = account.address
    this.chainId = process.env.VUE_APP_CHAIN
    this._getEthers()
  }

  /**
   * 断开连接 重置钱包
   */
  async disconnect() {
    this._initWallet()
  }

  /**
   * 获取chain Id
   * @return {Promise<number>}
   */
  async getChainId() {
    return this.chainId
  }

  // 获取提供者和签名者
  _getEthers() {
    this._provider = new ethers.providers.JsonRpcProvider(
      process.env.VUE_APP_RPC
      // process.env.VUE_APP_CHAIN
    )
    this._signer = this._provider.getSigner()
  }

  /**
   * 批准配额
   * @param {String} ERC20 - ERC20地址
   * @param {String} spender - 花费者地址
   * @param {String} [amount] - 要配额的金额 单位wei 默认为最大值
   * @return {Promise} 结果
   */
  approve(ERC20, spender, amount = '0x' + 'f'.repeat(64)) {
    // const contract = new ethers.Contract(ERC20, CommonERC20, this._signer)
    // return contract.approve(spender, amount)
    const iface = new ethers.utils.Interface(abi.commonERC20)
    const data = iface.encodeFunctionData('approve', [spender, amount])

    const tx = {
      from: this.account,
      to: ERC20,
      value: '0x00',
      data: data
    }

    return this.sendTx(tx)
  }

  /**
   * 获取路径
   * @param {String} type - 路径类型  值在 PATHTYPEARRAY 里定义
   * @param {Number} index - 路径变量  >=0 int类型
   * @return {String} 路径值
   * @private
   */
  _getPath(type, index) {
    return this.PATHTYPEOBJ[type].replace('i', index)
  }

  /**
   * 获取账户信息
   * @param {String} type - 路径类型  值在 PATHTYPEARRAY 里定义
   * @param {Number} index - 路径变量  >=0 int类型
   * @return {Promise<Object>} account - 账户信息
   * @return {String} account.address - 地址
   * @return {String} account.publicKey - 公钥
   */
  getAccount(type, index) {
    return new Promise((resolve, reject) => {
      this._connector
        .getAddress(this._getPath(type, index))
        .then(account => {
          this._paths[account.address] = this._getPath(type, index)
          resolve(account)
        })
        .catch(err => {
          if (err.statusCode !== undefined) {
            reject(throwError(err.statusCode, err))
          } else {
            reject(throwError(err.name, err))
          }
        })
    })
  }

  /**
   * 设置当前地址
   * @param address
   */
  setAccount(address) {
    this.account = address
  }

  /**
   * 发交易
   * @param {Object} data - 交易体
   * @param {string} data.from - 发出者地址
   * @param {string} data.value - eth数量 wei
   * @param {string} data.to - 接收者地址
   * @param {string} data.data - 合约数据 uint256
   * @param {string} [data.gasPrice] - gas Price 单位wei
   * @param {string} [data.nonce] - nonce
   * @param {string} [data.gas] - gas
   * @return {Promise} - 交易成功或失败
   */
  async sendTx(data) {
    if (data.nonce === undefined) {
      data.nonce = await this.getNonce()
    }
    // ledger 的nonce 必须是number类型  小狐狸是string类型
    data.nonce = Number(data.nonce)
    if (data.gasPrice === undefined) {
      const gasPrice = await this.getGasPrice()
      data.gasPrice = gasPrice.toHexString()
    }
    if (data.gas === undefined) {
      const gas = await this.estimateGas(data)
      data.gas = gas.toHexString()
    }
    const txData = JSON.parse(JSON.stringify(data))
    delete txData.from
    delete txData.gas

    // 小狐狸的gasLimit 和 gas 是反的  这里要换回来
    txData.gasLimit = data.gas

    const txHex = ethers.utils.serializeTransaction(txData)

    // 签名
    let signature
    try {
      signature = await this._connector.signTransaction(
        this._paths[data.from],
        txHex.slice(2)
      )
    } catch (err) {
      if (err.statusCode !== undefined) {
        throw throwError(err.statusCode, err)
      } else {
        throw throwError(err.name, err)
      }
    }

    const sign = {
      v: Number('0x' + signature.v),
      r: '0x' + signature.r,
      s: '0x' + signature.s
    }

    const tx = ethers.utils.serializeTransaction(txData, sign)

    // 发交易
    return new Promise((resolve, reject) => {
      this._provider
        .sendTransaction(tx)
        .then(res => {
          resolve(res.hash)
        })
        .catch(err => {
          reject(err)
        })
    })
  }
}
